import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/constants';

export default function ReferralsTab({ user }) {
  const [upcomingBonuses, setUpcomingBonuses] = useState([]);
  const [allReferrals, setAllReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('upcoming'); // 'upcoming' or 'all'

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        setLoading(true);
        
        // Fetch upcoming bonuses
        const upcomingRes = await fetch(`${API_BASE}/api/admin/referrals/upcoming`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });

        if (!upcomingRes.ok) {
          throw new Error(`Failed to fetch upcoming bonuses: ${upcomingRes.status}`);
        }

        const upcomingData = await upcomingRes.json();
        if (upcomingData.success) {
          setUpcomingBonuses(upcomingData.upcoming_bonuses || []);
        }

        // Fetch all referrals
        const allRes = await fetch(`${API_BASE}/api/admin/referrals`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });

        if (!allRes.ok) {
          throw new Error(`Failed to fetch all referrals: ${allRes.status}`);
        }

        const allData = await allRes.json();
        if (allData.success) {
          setAllReferrals(allData.referrals || []);
        }

      } catch (e) {
        console.error('Error fetching referral data:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchReferralData();
    }
  }, [user]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getBonusStatus = (bonusDate) => {
    if (!bonusDate) return { status: 'pending', text: 'Pending', color: 'var(--symbolica-muted)' };
    
    const today = new Date();
    const bonus = new Date(bonusDate);
    const diffTime = bonus - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', text: 'Overdue', color: '#dc2626' };
    } else if (diffDays === 0) {
      return { status: 'due', text: 'Due Today', color: '#f59e0b' };
    } else if (diffDays <= 30) {
      return { status: 'upcoming', text: `${diffDays} days`, color: '#f59e0b' };
    } else {
      return { status: 'future', text: `${diffDays} days`, color: 'var(--symbolica-muted)' };
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: 'var(--symbolica-muted)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid var(--symbolica-black)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading referral data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: '#dc2626'
      }}>
        Error loading referral data: {error}
      </div>
    );
  }

  const currentData = activeView === 'upcoming' ? upcomingBonuses : allReferrals;

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 className="content-title" style={{ marginBottom: '0.5rem' }}>
          Referral Management
        </h2>
        <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
          Track employee referrals and upcoming bonus payments
        </p>
      </div>

      {/* View Toggle */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          background: '#F0EDE6',
          padding: '4px',
          borderRadius: '8px',
          width: 'fit-content'
        }}>
          <button
            onClick={() => setActiveView('upcoming')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: activeView === 'upcoming' ? 'var(--symbolica-black)' : 'transparent',
              color: activeView === 'upcoming' ? 'white' : 'var(--symbolica-muted)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Upcoming Bonuses ({upcomingBonuses.length})
          </button>
          <button
            onClick={() => setActiveView('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: activeView === 'all' ? 'var(--symbolica-black)' : 'transparent',
              color: activeView === 'all' ? 'white' : 'var(--symbolica-muted)',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            All Referrals ({allReferrals.length})
          </button>
        </div>
      </div>

      {/* Data Table */}
      {currentData.length === 0 ? (
        <div style={{
          background: '#F6F4F0',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>
            {activeView === 'upcoming' ? '📅' : ''}
          </div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
            {activeView === 'upcoming' ? 'No upcoming bonuses' : 'No referrals found'}
          </h3>
          <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
            {activeView === 'upcoming' 
              ? 'All referral bonuses have been processed or no referrals are due soon.'
              : 'No employees have been referred yet.'
            }
          </p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#F6F4F0',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)', margin: 0 }}>
              {activeView === 'upcoming' ? 'Upcoming Referral Bonuses' : 'All Employee Referrals'}
            </h3>
          </div>
          
          <div style={{ padding: '0' }}>
            {currentData.map((item, index) => {
              const bonusStatus = getBonusStatus(item.referral_bonus_date);
              
              return (
                <div
                  key={item.employee_email}
                  style={{
                    padding: '1.5rem',
                    borderBottom: index < currentData.length - 1 ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        color: 'var(--symbolica-black)', 
                        margin: 0 
                      }}>
                        {item.employee_name}
                      </h4>
                      <span style={{
                        fontSize: '12px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: 'var(--symbolica-warm-border)',
                        color: 'var(--symbolica-muted)'
                      }}>
                        {item.title}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                      <div>
                        <strong>Started:</strong> {formatDate(item.start_date)}
                      </div>
                      <div>
                        <strong>Country:</strong> {item.country}
                      </div>
                      {activeView === 'upcoming' && (
                        <div>
                          <strong>Referrer:</strong> {item.referrer_name}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: 'var(--symbolica-muted)', 
                      marginBottom: '4px' 
                    }}>
                      Referral Bonus
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: bonusStatus.color,
                      marginBottom: '4px'
                    }}>
                      {formatDate(item.referral_bonus_date)}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: bonusStatus.color,
                      fontWeight: '500'
                    }}>
                      {bonusStatus.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginTop: '2rem' 
      }}>
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0369a1', marginBottom: '0.5rem' }}>
            {allReferrals.length}
          </div>
          <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>
            Total Referrals
          </div>
        </div>
        
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d97706', marginBottom: '0.5rem' }}>
            {upcomingBonuses.length}
          </div>
          <div style={{ fontSize: '14px', color: '#d97706', fontWeight: '500' }}>
            Upcoming Bonuses
          </div>
        </div>
        
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#059669', marginBottom: '0.5rem' }}>
            {upcomingBonuses.filter(b => {
              const bonus = new Date(b.referral_bonus_date);
              const today = new Date();
              const diffDays = Math.ceil((bonus - today) / (1000 * 60 * 60 * 24));
              return diffDays <= 30 && diffDays >= 0;
            }).length}
          </div>
          <div style={{ fontSize: '14px', color: '#059669', fontWeight: '500' }}>
            Due in 30 Days
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
